import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { useAuth } from '../contexts/AuthContext';
import { submitKeysOf } from '../components/workflow/useWorkflowPerms';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import ReviewerSelect from '../components/workflow/ReviewerSelect';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';
import { btnFieldAction } from '../components/workflow/constants';
import EngagementLeadsBox from '../components/workflow/EngagementLeadsBox';
import CompanyTabBar from '../components/workflow/CompanyTabBar';
import { engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import { engagementDisplayName } from '../utils/engagementName';
import { Qc35ChecklistForm } from '../components/workflow/Qc35Checklist';
import Qc35ScreenshotExample, { QC35_SCREENSHOT_HINT } from '../components/workflow/Qc35ScreenshotExample';
import { qc35AnswerGaps, applyQc35AutoAnswers } from './qc35Questions';

// QC35 — submission form (docs/modules/workflow-qc35.md; separate from Signed
// FS). No signing date — approval never touches the engagement sign-off. The
// File archival checklist below IS the QC35 — there is no document to upload;
// the filled form is downloadable as a PDF once approved. The tech-tools /
// restatement / independence questions are not asked here — they are asked
// once, on the Signed Financial Statements submission.
//
// One page files one or several companies of the same client group and FYE
// year — one tab per company (archival date, CaseWare screenshot, checklist),
// one request per company (POST /api/workflow/qc35/batch); Remarks and the
// Reviewer are shared.
//
// Archival date：頂層必填——它就是 CaseWare lock-down 日，核准＝涵蓋案歸檔完成
// （Archived 不是獨立的一關，也沒有人要回 Engagements 頁手填日期）。

// Archival date 的說明——New／Detail 的 Modify modal／Management 記錄表單
// 三面同一句（同一個欄位講三種話會像三個不同的日期）。
export const ARCHIVAL_DATE_HINT = 'The CaseWare lock-down date — every covered engagement counts as archived on this date. It cannot be before the sign-off date.';

// 歸檔日 ≥ 簽署日（後端同一把尺）——選到日期的當下就講清楚是哪一案擋的：
// 三面都把它算出來交給 `Field` 的 `error=` 就地顯示，送出時再擋一次。
// engagements 需帶 signOffDate（缺的略過，後端仍是最後防線）。
// signOffDate 可能是 'YYYY-MM-DD' 或完整 ISO 時戳，切前十碼再比。
export const archivalDateError = (archivalDate, engagements = []) => {
  if (!archivalDate) return null;
  for (const en of engagements || []) {
    const signOff = en && en.signOffDate ? String(en.signOffDate).slice(0, 10) : null;
    if (signOff && archivalDate < signOff) {
      return `Archival date cannot be before the sign-off date of ${engagementDisplayName(en.engagementName, en.serviceDescription)} (${formatDate(signOff)}).`;
    }
  }
  return null;
};

// "Copy Answers From": the source tab's checklist, re-pinned to the
// destination's own QC context (EQR row, guidelines) — the derived header and
// date rows read the destination engagement anyway.
export const copyQc35Answers = (srcAnswers, dstCtx) => applyQc35AutoAnswers({ ...(srcAnswers || {}) }, dstCtx);

// The "Copy Answers From" chip row, shown under the checklist heading.
const Qc35CopyRow = ({ sources, onCopy }) => {
  if (sources.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      <span>Copy checklist answers from:</span>
      {sources.map(({ id, label }) => (
        <button key={id} type="button" onClick={() => onCopy(id)} className={btnFieldAction}
          title={`Make this tab's checklist match ${label}'s current answers`}>
          {label}
        </button>
      ))}
    </div>
  );
};

// The checklist block — heading, "Copy checklist answers from" chips (several
// tabs only) and the checklist itself — shared by this page and the Management
// record form so both read the same.
export const Qc35ChecklistBlock = ({ answers, onChange, engagements, onContext, copySources = [], onCopy }) => (
  <div className="space-y-2">
    <div className="text-sm font-medium text-slate-700">
      File Archival Checklist <InfoTip text="The checklist printed on Form QC35 — the client name and year-end are filled in from this engagement. Hover a blue info icon for the guidance printed on the Word form, or click it to keep the guidance open under the question." />
    </div>
    <Qc35CopyRow sources={copySources} onCopy={onCopy} />
    <Qc35ChecklistForm answers={answers} onChange={onChange} engagements={engagements} onContext={onContext} />
  </div>
);

const emptyCompany = () => ({ archivalDate: '', screenshots: [], answers: {} });

// Duplicate guard, mirrored from the server: one open/approved QC35 per
// engagement. Returns the blocking reason, or null when clear.
const qc35BlockedOf = (en) => {
  if (!en?.qc35Coverage) return null;
  if (en.qc35Coverage.approved) return `QC35 Form already approved for ${engagementDisplayName(en.engagementName, en.serviceDescription)} (${en.qc35Coverage.approved.requestNo}) — corrections go through QC35 Form Amendment`;
  if (en.qc35Coverage.pending) return `QC35 Form already in review for ${engagementDisplayName(en.engagementName, en.serviceDescription)} (${en.qc35Coverage.pending.requestNo})`;
  return null;
};

// One company's tab. Every selected company keeps its panel mounted (hidden
// when not active) so the checklist and its QC context survive tab switches.
const Qc35CompanyPanel = ({
  engagement, value, onChange, hidden, onContext, validationValue, copySources, onCopy,
}) => {
  const set = (key, v) => onChange({ [key]: v });
  const blocked = qc35BlockedOf(engagement);
  const handleContext = useCallback((ctx) => onContext(engagement.id, ctx), [engagement.id, onContext]);
  return (
    <div role="tabpanel" hidden={hidden} className={hidden ? 'hidden' : 'space-y-4'}>
      <FormValidationProvider value={validationValue}>
        <EngagementLeadsBox engagement={engagement} />
        {blocked && (
          <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
            {blocked}
            {engagement.qc35Coverage?.pending && (
              <>
                {' — '}
                <RefLink to={`/workflow/qc35/${engagement.qc35Coverage.pending.id}`} className="font-medium underline hover:text-amber-900">
                  open {engagement.qc35Coverage.pending.requestNo}
                </RefLink>
              </>
            )}
          </div>
        )}

        {/* 歸檔日＝CaseWare lock-down 日——核准當下就把涵蓋案標記歸檔。 */}
        <Field label="Archival Date" required field="archivalDate" hint={ARCHIVAL_DATE_HINT} error={archivalDateError(value.archivalDate, [engagement])}>
          <input type="date" value={value.archivalDate} onChange={e => set('archivalDate', e.target.value)} className={inputCls} />
        </Field>

        {/* Form QC35（May 2026）要求附上 CaseWare 的鎖檔截圖——每家各自的檔。 */}
        <Field label="CaseWare Audit Working Papers Screenshot" required field="screenshot" hint={QC35_SCREENSHOT_HINT}>
          <FilePicker
            multiple
            accept=".png,.jpg,.jpeg,.gif,.webp"
            onChange={e => set('screenshots', Array.from(e.target.files || []))}
            files={value.screenshots}
          />
          <Qc35ScreenshotExample />
        </Field>

        <Qc35ChecklistBlock
          answers={value.answers}
          onChange={v => set('answers', v)}
          engagements={[engagement]}
          onContext={handleContext}
          copySources={copySources}
          onCopy={onCopy}
        />
      </FormValidationProvider>
    </div>
  );
};

const WorkflowQc35New = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [engagements, setEngagements] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);

  const [engagementIds, setEngagementIds] = useState([]);
  const [companies, setCompanies] = useState({}); // { [id]: { archivalDate, screenshots, answers } }
  // QC6/QC7 現況（評級＋EQR）——每家的 checklist 選案後抓回來，逐題必填要看它。
  const [contexts, setContexts] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');

  const canSubmit = submitKeysOf('qc35').some(hasPermission);
  const engOf = (id) => engagements.find(e => e.id === id) || null;
  // Company name, plus service / description when two tabs share the company.
  const labels = engagementLabelsOf(engagementIds.map(engOf));
  const nameOf = (id) => labels[id]?.text || `#${id}`;
  const selectedAll = engagements.filter(e => engagementIds.includes(e.id));
  const multi = engagementIds.length > 1;

  useEffect(() => {
    if (!canSubmit) return;
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/qc35/options');
        setEngagements(data.engagements || []);
      } catch (e) { setError(e.message); }
    })();
  }, [canSubmit]);

  const updateSelection = (ids) => {
    setError(null);
    setEngagementIds(ids);
    setCompanies(prev => Object.fromEntries(ids.map(id => [id, prev[id] || emptyCompany()])));
    setActiveId(cur => (ids.includes(cur) ? cur : (ids[0] || null)));
  };
  const patchCompany = (id, part) => setCompanies(prev => ({ ...prev, [id]: { ...prev[id], ...part } }));
  const onContext = useCallback((id, ctx) => setContexts(prev => ({ ...prev, [id]: ctx })), []);
  useEffect(() => { setNotice(null); }, [activeId]);

  const copyFrom = (srcId, dstId) => {
    patchCompany(dstId, { answers: copyQc35Answers(companies[srcId]?.answers, contexts[dstId] || null) });
    setNotice(`This tab's checklist now matches ${nameOf(srcId)} — check the rows that differ for this company (time involvement, EQR).`);
  };

  // Each company's own blanks (key = the field= key inside its tab).
  const companyGaps = (id) => {
    const c = companies[id] || emptyCompany();
    return [
      ...(c.archivalDate ? [] : [{ key: 'archivalDate', label: 'Archival Date' }]),
      ...(c.screenshots.length > 0 ? [] : [{ key: 'screenshot', label: 'CaseWare Screenshot' }]),
      // 逐題必填由題庫講（checklist 的格自己標紅，key＝answers.<id>）；EQR 時數
      // 與未達指引的 Comments 看這家的 QC 現況。
      ...qc35AnswerGaps(c.answers, contexts[id] || null),
    ];
  };

  const gaps = useMemo(() => [
    ...(engagementIds.length === 0 ? [{ key: 'engagements', label: 'Engagement' }] : []),
    ...engagementIds.flatMap(id => companyGaps(id).map(g => ({
      key: `${id}:${g.key}`,
      label: multi ? `${nameOf(id)} — ${g.label}` : g.label,
    }))),
    ...(reviewerStaffId ? [] : [{ key: 'reviewerName', label: 'Reviewer' }]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [engagementIds, companies, contexts, reviewerStaffId, multi, engagements]);
  const validation = useFormValidation(gaps);

  // Per-tab provider: that company's marks only, prefix stripped.
  const panelValidation = (id) => {
    const byKey = {};
    const prefix = `${id}:`;
    for (const [k, g] of Object.entries(validation.providerValue.byKey)) {
      if (k.startsWith(prefix)) byKey[k.slice(prefix.length)] = g;
    }
    return { byKey };
  };

  const blockedReasons = selectedAll.map(qc35BlockedOf).filter(Boolean);
  const crossBlocked = engagementBlockReasons(selectedAll, 'qc35').length > 0;
  const dateErrorOf = (id) => archivalDateError(companies[id]?.archivalDate, [engOf(id)].filter(Boolean));

  const tabTag = (id) => {
    if (qc35BlockedOf(engOf(id))) return { text: 'Blocked', tone: 'todo' };
    const missing = companyGaps(id).length + (dateErrorOf(id) ? 1 : 0);
    if (missing === 0) return { text: 'Ready', tone: 'done' };
    return validation.attempted ? { text: `${missing} Missing`, tone: 'todo' } : { text: 'Incomplete', tone: 'muted' };
  };

  const submit = async () => {
    if (!validation.validate()) {
      setError(null);
      const first = engagementIds.find(id => companyGaps(id).length > 0);
      if (first) setActiveId(first);
      return;
    }
    const dateBad = engagementIds.find(id => dateErrorOf(id));
    if (dateBad) { setActiveId(dateBad); setError(dateErrorOf(dateBad)); return; }
    if (blockedReasons.length > 0) {
      setError(`These engagements cannot receive a QC35 Form submission: ${blockedReasons.join('; ')}.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('items', JSON.stringify(engagementIds.map(id => ({
        engagementId: id,
        archivalDate: companies[id].archivalDate,
        answers: companies[id].answers,
      }))));
      form.set('remarks', remarks);
      form.set('reviewerStaffId', reviewerStaffId);
      engagementIds.forEach(id => companies[id].screenshots.forEach(f => form.append(`screenshot_${id}`, f)));

      const data = await fetchJSON('/api/workflow/qc35/batch', { method: 'POST', body: form });
      const created = (data.results || []).filter(r => r.request);
      const failed = (data.results || []).filter(r => r.error);
      if (failed.length === 0) {
        navigate(`/workflow/qc35/${created[0].request.id}`);
        return;
      }
      // Part of it went through: drop the filed companies, keep the failed
      // tabs with the reason so they can be sent again.
      updateSelection(failed.map(r => r.engagementId));
      setError(`${created.length > 0 ? `${created.map(r => r.request.requestNo).join(', ')} submitted. ` : ''}`
        + `Not submitted: ${failed.map(r => `${nameOf(r.engagementId)} (${r.error})`).join('; ')}.`);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/qc35"
      backLabel="QC35 Form"
      title="New QC35 Form Request"
      subtitle="Complete the file archival checklist and attach the CaseWare screenshot."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={blockedReasons.length > 0 || crossBlocked}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement(s)">
          <Field label="Engagement(s)" required field="engagements" hint={<>Only engagements you are a member of whose Signed Financial Statements has been approved are listed. <InfoTip text="Each company becomes its own request with its own reviewer decision. Companies submitted together must belong to the same client group with an FYE in the same year." /></>}>
            <GroupedEngagementPicker
              engagements={engagements}
              selectedIds={engagementIds}
              onChange={updateSelection}
            />
            <EngagementBlockers selected={selectedAll} flow="qc35" />
          </Field>
        </FormSection>

        <FormSection
          step={2}
          title="Submission"
          hint={multi ? 'One tab per company — each tab is filed as its own request.' : null}
        >
          {engagementIds.length === 0 ? (
            <p className="text-sm text-slate-500">Select the engagement(s) above first.</p>
          ) : (
            <>
              {multi && (
                <CompanyTabBar
                  items={engagementIds.map(id => ({ id, label: labels[id]?.label || `#${id}`, sub: labels[id]?.sub, title: engagementDisplayName(engOf(id)?.engagementName, engOf(id)?.serviceDescription), tag: tabTag(id) }))}
                  activeId={activeId}
                  onSelect={setActiveId}
                />
              )}
              {notice && <p className="text-xs text-emerald-700">{notice}</p>}
              {engagementIds.map(id => {
                const en = engOf(id);
                if (!en || !companies[id]) return null;
                return (
                  <Qc35CompanyPanel
                    key={id}
                    engagement={en}
                    value={companies[id]}
                    onChange={part => patchCompany(id, part)}
                    hidden={id !== activeId}
                    onContext={onContext}
                    validationValue={panelValidation(id)}
                    copySources={multi ? engagementIds.filter(x => x !== id).map(x => ({ id: x, label: nameOf(x) })) : []}
                    onCopy={srcId => copyFrom(srcId, id)}
                  />
                );
              })}
            </>
          )}
        </FormSection>

        <FormSection step={3} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={4} title="Reviewer" hint={multi ? 'One Partner reviews every company on this page; each request is approved or returned on its own.' : null}>
          <Field label="Reviewer" required field="reviewerName"
            hint="You pick who reviews this submission, from the firm's Partners.">
            <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowQc35New;
